import React, { useState } from 'react';
import { isVideoUrl } from '../../utils/fileValidation';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Cancel,
  ThumbUp,
  ThumbDown,
  LocationOn,
  Timer,
  SmartToy,
  PictureAsPdf,
  Draw
} from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ReactMarkdown from 'react-markdown';
import api from '../../services/api';
import DigitalSignatureCapture from '../signatures/DigitalSignatureCapture';
import DocumentFooter from '../documents/DocumentFooter';
import DialogCloseButton from '../common/DialogCloseButton';
import {
  SectionWrapper,
  SectionHeader,
  DOCUMENT_COLORS,
} from '../documents/documentStyles';

// Fields like photos arrive either as a JSON string (straight from the DB) or
// already parsed (GET /simple-verifications/:id presigns photo URLs and
// returns a real array). JSON.parse on an already-parsed array throws, which
// crashed this whole page into the error boundary — accept both shapes.
const parseMaybeJson = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const normalizeArray = (value) => (Array.isArray(value) ? value : []);
const normalizeObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

/**
 * Simple Verification Report Display
 * Shows AI-generated verification report with approval/dispute options.
 * With `previewMode` it renders the identical client view from unsubmitted
 * workflow state so verifiers can see the final report before submitting.
 */
const SimpleVerificationReport = ({ verification, isClient, onApprove, onDispute, verifierInfo, previewMode = false }) => {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [rating, setRating] = useState(5);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [approvalStep, setApprovalStep] = useState(0); // 0 = rate, 1 = sign
  const [showSignatureCapture, setShowSignatureCapture] = useState(false);

  const checklistResults = normalizeArray(parseMaybeJson(verification.checklistResults, []));
  const photos = normalizeArray(parseMaybeJson(verification.photos, []));
  const recommendations = normalizeArray(parseMaybeJson(verification.aiRecommendations, []));
  const qualityBreakdown = normalizeObject(parseMaybeJson(verification.aiQualityBreakdown, {}));
  const aiDecisionSupport = verification.aiDecisionSupport;

  const getStatusColor = () => {
    if (verification.status === 'approved') return 'success';
    if (verification.status === 'disputed') return 'error';
    if (verification.status === 'completed') return 'warning';
    return 'default';
  };

  const getStatusIcon = () => {
    if (verification.status === 'approved') return <CheckCircle />;
    if (verification.status === 'disputed') return <Cancel />;
    return <Warning />;
  };

  const handleApproveNext = () => {
    // Move from rating step to signature step
    setShowApproveDialog(false);
    setShowSignatureCapture(true);
  };

  const handleSignatureComplete = (signatureData) => {
    setShowSignatureCapture(false);
    setApprovalStep(0);
    onApprove({ approvalNotes, rating, signature: signatureData });
  };

  const handleApproveDialogClose = () => {
    setShowApproveDialog(false);
    setApprovalStep(0);
  };

  const handleDispute = () => {
    onDispute({ disputeReason });
    setShowDisputeDialog(false);
  };

  // The PDF is generated server-side from the same branded template used for
  // all Eqaya documents, so every client receives an identical report format.
  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const response = await api.get(`/simple-verifications/${verification.id}/pdf`, {
        responseType: 'blob',
        timeout: 120000
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `eqaya-verification-report-${verification.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to download the report PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      {previewMode && (
        <Alert severity="info" icon={<VisibilityOutlinedIcon />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="600">
            Preview — this is exactly how the report will appear to your client.
          </Typography>
          <Typography variant="body2">
            Nothing has been sent yet. Close the preview to keep editing, or submit when you're happy with it.
          </Typography>
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: APPBAR_GRADIENT_INLINE, color: 'white', borderRadius: 2 }}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Verification Report
        </Typography>
        <Typography variant="h6" gutterBottom>
          {verification.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={getStatusIcon()}
            label={(verification.status || 'completed').toUpperCase()}
            color={getStatusColor()}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
          <Chip
            icon={<LocationOn sx={{ color: 'white !important' }} />}
            label="On-site Verified"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
          {Boolean(verification.verificationDuration) && (
            <Chip
              icon={<Timer sx={{ color: 'white !important' }} />}
              label={`${verification.verificationDuration} minutes`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          )}
          <Chip
            icon={<SmartToy sx={{ color: 'white !important' }} />}
            label={`AI Confidence: ${Math.round((verification.aiConfidenceScore || 0) * 100)}%`}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
        </Box>
      </Paper>

      {/* Export Actions — hidden in preview: the server-side PDF renders only submitted reports */}
      {!previewMode && (
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={exportingPDF ? <CircularProgress size={20} /> : <PictureAsPdf />}
          onClick={handleExportPDF}
          disabled={exportingPDF}
          sx={{
            borderColor: DOCUMENT_COLORS.brand,
            color: DOCUMENT_COLORS.brand,
            '&:hover': {
              borderColor: DOCUMENT_COLORS.brandDark,
              bgcolor: 'rgba(37, 99, 235, 0.04)'
            }
          }}
        >
          {exportingPDF ? 'Generating PDF...' : 'Export to PDF'}
        </Button>
      </Box>
      )}

      {/* AI Decision Support (for clients) */}
      {isClient && aiDecisionSupport && verification.status === 'completed' && (
        <Alert
          severity={
            aiDecisionSupport.recommendation === 'APPROVE' ? 'success' :
            aiDecisionSupport.recommendation === 'REJECT' ? 'error' : 'warning'
          }
          icon={<SmartToy />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" fontWeight="600" gutterBottom>
            AI Recommendation: {aiDecisionSupport.recommendation.replace('_', ' ')}
          </Typography>
          {normalizeArray(aiDecisionSupport.reasoning).map((reason, index) => (
            <Typography key={index} variant="body2">
              {"\u2022"} {reason}
            </Typography>
          ))}
        </Alert>
      )}

      {/* AI Summary */}
      <SectionWrapper>
        <SectionHeader
          icon={<SmartToy fontSize="small" />}
          title="AI Summary"
        />
        <Typography variant="body1" paragraph>
          {verification.aiSummary}
        </Typography>
        <Chip
          label={`Risk Level: ${(verification.aiRiskLevel || 'low').toUpperCase()}`}
          color={
            verification.aiRiskLevel === 'low' ? 'success' :
            verification.aiRiskLevel === 'high' ? 'error' : 'warning'
          }
        />
      </SectionWrapper>

      {/* Checklist Results */}
      <SectionWrapper>
        <SectionHeader
          icon={<ChecklistOutlinedIcon fontSize="small" />}
          title="Checklist Results"
        />
        {checklistResults.map((item, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
              {item.verified ? (
                <CheckCircle sx={{ color: '#4caf50', mt: 0.5 }} />
              ) : (
                <Warning sx={{ color: '#ff9800', mt: 0.5 }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="500">
                  {item.item}
                </Typography>
                {item.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {item.notes}
                  </Typography>
                )}
                {item.aiConfidence && (
                  <Chip
                    label={`AI: ${Math.round(item.aiConfidence * 100)}% confident`}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </SectionWrapper>

      {/* Photo Evidence */}
      <SectionWrapper>
        <SectionHeader
          icon={<PhotoLibraryOutlinedIcon fontSize="small" />}
          title={`Photo Evidence (${photos.length})`}
        />
        <Grid container spacing={2}>
          {photos.map((photo, index) => {
            const mediaUrl = photo.url || photo.photoUrl || photo.previewUrl;
            const isVideo = photo.isVideo || isVideoUrl(mediaUrl);
            return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                {isVideo ? (
                  <Box
                    component="video"
                    src={mediaUrl}
                    controls
                    sx={{ width: '100%', height: 200, objectFit: 'cover', bgcolor: '#000' }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={mediaUrl}
                    alt={photo.name || `Verification photo ${index + 1}`}
                    sx={{ width: '100%', height: 200, objectFit: 'cover' }}
                  />
                )}
                <Box sx={{ p: 1.5 }}>
                  {photo.timestamp && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(photo.timestamp).toLocaleString()}
                    </Typography>
                  )}
                  {photo.aiAnalysis && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {photo.aiAnalysis.autoCaption}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
            );
          })}
        </Grid>
      </SectionWrapper>

      {/* AI Recommendations */}
      {recommendations.length > 0 && (
        <SectionWrapper>
          <SectionHeader
            icon={<TipsAndUpdatesOutlinedIcon fontSize="small" />}
            title="AI Recommendations"
          />
          {recommendations.map((rec, index) => (
            <Alert key={index} severity="info" sx={{ mb: 1 }}>
              {rec}
            </Alert>
          ))}
        </SectionWrapper>
      )}

      {/* Verifier Notes */}
      {verification.verifierNotes && (
        <SectionWrapper>
          <SectionHeader
            icon={<StickyNote2OutlinedIcon fontSize="small" />}
            title="Verifier Notes"
          />
          <Typography variant="body1">
            {verification.verifierNotes}
          </Typography>
        </SectionWrapper>
      )}

      {/* Quality Score */}
      {verification.aiQualityScore && (
        <SectionWrapper>
          <SectionHeader
            icon={<SpeedOutlinedIcon fontSize="small" />}
            title="Verification Quality Score"
          />
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h2" fontWeight="700" color="primary">
              {verification.aiQualityScore}/100
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI Quality Assessment
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="600">
                  {qualityBreakdown.photoQuality || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Photo Quality
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="600">
                  {qualityBreakdown.checklistCompletion || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Checklist Completion
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="600">
                  {qualityBreakdown.timeEfficiency || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Time Efficiency
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </SectionWrapper>
      )}

      {/* Full AI Report */}
      <SectionWrapper>
        <SectionHeader
          icon={<DescriptionOutlinedIcon fontSize="small" />}
          title="Full Report"
        />
        <Box sx={{
          '& h1, & h2, & h3': { fontWeight: 600, mt: 2, mb: 1 },
          '& p': { mb: 1 },
          '& ul, & ol': { pl: 3 }
        }}>
          <ReactMarkdown>{verification.aiReportContent}</ReactMarkdown>
        </Box>
      </SectionWrapper>

      {/* Actions (for clients) */}
      {isClient && verification.status === 'completed' && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            size="large"
            startIcon={<ThumbDown />}
            onClick={() => setShowDisputeDialog(true)}
          >
            Dispute
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="success"
            size="large"
            startIcon={<ThumbUp />}
            onClick={() => setShowApproveDialog(true)}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
            }}
          >
            Approve & Release Payment
          </Button>
        </Box>
      )}

      <DocumentFooter documentType="verification report" />

      {/* Approve Dialog — Step 1: Rate & Review */}
      <Dialog open={showApproveDialog} onClose={handleApproveDialogClose} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={handleApproveDialogClose} />
        <DialogTitle>Approve Verification</DialogTitle>
        <DialogContent>
          <Stepper activeStep={0} sx={{ mb: 3 }}>
            <Step><StepLabel>Rate & Review</StepLabel></Step>
            <Step><StepLabel>Sign Acknowledgment</StepLabel></Step>
          </Stepper>
          <Typography variant="body2" color="text.secondary" paragraph>
            By approving this verification, {verification.cost} will be released to the verifier.
            You will be asked to sign a digital acknowledgment in the next step.
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Rate this verification:
          </Typography>
          <Rating
            value={rating}
            onChange={(e, newValue) => setRating(newValue)}
            size="large"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            multiline
            rows={3}
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApproveDialogClose}>Cancel</Button>
          <Button onClick={handleApproveNext} variant="contained" color="success" startIcon={<Draw />}>
            Continue to Sign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog — Step 2: Digital Signature */}
      <DigitalSignatureCapture
        open={showSignatureCapture}
        onClose={() => { setShowSignatureCapture(false); setApprovalStep(0); }}
        onSignatureComplete={handleSignatureComplete}
        documentType="verification_report"
        documentData={verification}
        signerName=""
        signerEmail=""
      />

      {/* Dispute Dialog */}
      <Dialog open={showDisputeDialog} onClose={() => setShowDisputeDialog(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setShowDisputeDialog(false)} />
        <DialogTitle>Dispute Verification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please explain why you're disputing this verification. Our team will review and mediate.
          </Typography>
          <TextField
            fullWidth
            label="Dispute Reason"
            multiline
            rows={4}
            value={disputeReason}
            onChange={(e) => setDisputeReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisputeDialog(false)}>Cancel</Button>
          <Button onClick={handleDispute} variant="contained" color="error" disabled={!disputeReason.trim()}>
            Submit Dispute
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Inline gradient for the non-AppBar header
const APPBAR_GRADIENT_INLINE = 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)';

export default SimpleVerificationReport;
